import { c as _c } from "react/compiler-runtime";
import { useChains } from "@opensea/app-kit";
import { graphql, readFragment, useQuery } from "@opensea/graphql";
import { BottomSheetBody, BottomSheetFooter, BottomSheetHeader, BottomSheetHeaderTitle, Checkbox, FlexEnd, FlexStart, Label, SkeletonLine, TextBody } from "@opensea/ui-kit";
import { useState } from "react";
import { useTranslations } from "use-intl";
import { ActionTimeline } from "../../../components/ActionTimeline";
import { ActionTimelineFragment } from "../../../components/ActionTimeline/ActionTimeline.fragment";
import { Button } from "../../../components/Button";
import { TransactionFullScreen } from "../../../components/TransactionFullScreen";
import { NETWORK_ONLY_OPERATION_CONTEXT } from "../../../constants";
import { useAddress } from "../../../polychain/hooks/useAddress";
import { useAllActionsCompleted } from "../../hooks/useAllActionsCompleted";
import { useEndFlowOnAccountChange } from "../../hooks/useEndFlowOnAccountChange";
import { useFlowFinalTransactionStatus } from "../../hooks/useFlowFinalTransactionStatus";
const MakerOrdersCountFragment = graphql(`
  fragment MakerOrdersCount on MakerOrdersCountResponse {
    chain
    activeListings
    inactiveListings
    offers
  }
`);
const CancelAllOrdersQuery = graphql(`
    query CancelAllOrdersFlowQuery(
      $chain: ChainIdentifier!
      $address: Address!
    ) {
      cancelAllOrders(chain: $chain, address: $address) {
        actions {
          ...ActionTimeline
        }
      }
      makerOrdersCount(address: $address, chain: $chain) {
        ...MakerOrdersCount
      }
    }
  `, [ActionTimelineFragment, MakerOrdersCountFragment]);
export const CancelAllOrdersFlow = t0 => {
  const $ = _c(28);
  const {
    chain
  } = t0;
  const address = useAddress();
  const t = useTranslations("wallet.CancelAllOrdersFlow");
  const t1 = address ?? "";
  let t2;
  if ($[0] !== chain || $[1] !== t1) {
    t2 = {
      chain,
      address: t1
    };
    $[0] = chain;
    $[1] = t1;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const t3 = !address;
  let t4;
  if ($[3] !== t2 || $[4] !== t3) {
    t4 = {
      query: CancelAllOrdersQuery,
      variables: t2,
      pause: t3,
      context: NETWORK_ONLY_OPERATION_CONTEXT
    };
    $[3] = t2;
    $[4] = t3;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  const [t5] = useQuery(t4);
  const {
    data,
    fetching
  } = t5;
  useEndFlowOnAccountChange();
  const {
    getName: getChainName
  } = useChains();
  const [isUserAgreed, setIsUserAgreed] = useState(false);
  const [hasConfirmed, setHasConfirmed] = useState(false);
  const [allActionsCompleted] = useAllActionsCompleted();
  let t6;
  if ($[6] !== chain || $[7] !== getChainName) {
    t6 = getChainName(chain);
    $[6] = chain;
    $[7] = getChainName;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  const chainName = t6;
  if (fetching) {
    let t7;
    if ($[9] !== chainName) {
      t7 = <CancelAllOrdersFlowSkeleton chainName={chainName} />;
      $[9] = chainName;
      $[10] = t7;
    } else {
      t7 = $[10];
    }
    return t7;
  }
  if (allActionsCompleted) {
    let t7;
    if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
      t7 = <CancelAllOrdersStatus />;
      $[11] = t7;
    } else {
      t7 = $[11];
    }
    return t7;
  }
  let t7;
  if ($[12] !== chainName) {
    t7 = <CancelAllOrdersFlowHeader chainName={chainName} />;
    $[12] = chainName;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  const t8 = data?.makerOrdersCount?.[0];
  let t9;
  if ($[14] !== chainName || $[15] !== hasConfirmed || $[16] !== isUserAgreed || $[17] !== t8) {
    t9 = <CancelAllOrdersFlowBody chainName={chainName} hasStartedActions={hasConfirmed} isUserAgreed={isUserAgreed} makerOrdersCount={t8} setIsUserAgreed={setIsUserAgreed} />;
    $[14] = chainName;
    $[15] = hasConfirmed;
    $[16] = isUserAgreed;
    $[17] = t8;
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  let t10;
  if ($[19] !== data || $[20] !== hasConfirmed || $[21] !== isUserAgreed || $[22] !== t) {
    t10 = <BottomSheetFooter>{hasConfirmed && data?.cancelAllOrders.actions ? <ActionTimeline actions={data.cancelAllOrders.actions} /> : <FlexEnd><Button disabled={!isUserAgreed} onClick={() => setHasConfirmed(true)} variant="destructive">{t("confirmCancellation")}</Button></FlexEnd>}</BottomSheetFooter>;
    $[19] = data;
    $[20] = hasConfirmed;
    $[21] = isUserAgreed;
    $[22] = t;
    $[23] = t10;
  } else {
    t10 = $[23];
  }
  let t11;
  if ($[24] !== t10 || $[25] !== t7 || $[26] !== t9) {
    t11 = <>{t7}{t9}{t10}</>;
    $[24] = t10;
    $[25] = t7;
    $[26] = t9;
    $[27] = t11;
  } else {
    t11 = $[27];
  }
  return t11;
};
const CancelAllOrdersFlowBody = t0 => {
  const $ = _c(52);
  const {
    chainName,
    makerOrdersCount,
    isUserAgreed,
    setIsUserAgreed,
    hasStartedActions
  } = t0;
  const t = useTranslations("wallet.CancelAllOrdersFlow");
  let t1;
  if ($[0] !== makerOrdersCount) {
    t1 = readFragment(MakerOrdersCountFragment, makerOrdersCount);
    $[0] = makerOrdersCount;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const orderCounts = t1;
  let cancellationMessage;
  if (orderCounts) {
    const {
      activeListings,
      inactiveListings,
      offers
    } = orderCounts;
    const hasActiveListings = activeListings > 0;
    const hasInactiveListings = inactiveListings > 0;
    const hasOffers = offers > 0;
    if (hasActiveListings && hasInactiveListings && hasOffers) {
      let t2;
      if ($[2] !== activeListings || $[3] !== inactiveListings || $[4] !== offers || $[5] !== t) {
        t2 = t("attemptingToCancelActiveInactiveListingsAndOffersV2", {
          activeCount: activeListings,
          inactiveCount: inactiveListings,
          offersCount: offers
        });
        $[2] = activeListings;
        $[3] = inactiveListings;
        $[4] = offers;
        $[5] = t;
        $[6] = t2;
      } else {
        t2 = $[6];
      }
      cancellationMessage = t2;
    } else {
      if (hasActiveListings && hasInactiveListings) {
        let t2;
        if ($[7] !== activeListings || $[8] !== inactiveListings || $[9] !== t) {
          t2 = t("attemptingToCancelActiveAndInactiveListingsV2", {
            activeCount: activeListings,
            inactiveCount: inactiveListings
          });
          $[7] = activeListings;
          $[8] = inactiveListings;
          $[9] = t;
          $[10] = t2;
        } else {
          t2 = $[10];
        }
        cancellationMessage = t2;
      } else {
        if (hasActiveListings && hasOffers) {
          let t2;
          if ($[11] !== activeListings || $[12] !== offers || $[13] !== t) {
            t2 = t("attemptingToCancelActiveListingsAndOffersV2", {
              activeCount: activeListings,
              offersCount: offers
            });
            $[11] = activeListings;
            $[12] = offers;
            $[13] = t;
            $[14] = t2;
          } else {
            t2 = $[14];
          }
          cancellationMessage = t2;
        } else {
          if (hasInactiveListings && hasOffers) {
            let t2;
            if ($[15] !== inactiveListings || $[16] !== offers || $[17] !== t) {
              t2 = t("attemptingToCancelInactiveListingsAndOffersV2", {
                inactiveCount: inactiveListings,
                offersCount: offers
              });
              $[15] = inactiveListings;
              $[16] = offers;
              $[17] = t;
              $[18] = t2;
            } else {
              t2 = $[18];
            }
            cancellationMessage = t2;
          } else {
            if (hasActiveListings) {
              let t2;
              if ($[19] !== activeListings || $[20] !== t) {
                t2 = t("attemptingToCancelActiveListings", {
                  activeCount: activeListings
                });
                $[19] = activeListings;
                $[20] = t;
                $[21] = t2;
              } else {
                t2 = $[21];
              }
              cancellationMessage = t2;
            } else {
              if (hasInactiveListings) {
                let t2;
                if ($[22] !== inactiveListings || $[23] !== t) {
                  t2 = t("attemptingToCancelInactiveListings", {
                    inactiveCount: inactiveListings
                  });
                  $[22] = inactiveListings;
                  $[23] = t;
                  $[24] = t2;
                } else {
                  t2 = $[24];
                }
                cancellationMessage = t2;
              } else {
                if (hasOffers) {
                  let t2;
                  if ($[25] !== offers || $[26] !== t) {
                    t2 = t("attemptingToCancelOffers", {
                      offersCount: offers
                    });
                    $[25] = offers;
                    $[26] = t;
                    $[27] = t2;
                  } else {
                    t2 = $[27];
                  }
                  cancellationMessage = t2;
                } else {
                  let t2;
                  if ($[28] !== t) {
                    t2 = t("attemptingToCancelAll");
                    $[28] = t;
                    $[29] = t2;
                  } else {
                    t2 = $[29];
                  }
                  cancellationMessage = t2;
                }
              }
            }
          }
        }
      }
    }
  } else {
    let t2;
    if ($[30] !== t) {
      t2 = t("attemptingToCancelAll");
      $[30] = t;
      $[31] = t2;
    } else {
      t2 = $[31];
    }
    cancellationMessage = t2;
  }
  let t2;
  if ($[32] !== cancellationMessage) {
    t2 = <TextBody className="text-text-secondary">{cancellationMessage}</TextBody>;
    $[32] = cancellationMessage;
    $[33] = t2;
  } else {
    t2 = $[33];
  }
  let t3;
  if ($[34] !== isUserAgreed || $[35] !== setIsUserAgreed) {
    t3 = () => setIsUserAgreed(!isUserAgreed);
    $[34] = isUserAgreed;
    $[35] = setIsUserAgreed;
    $[36] = t3;
  } else {
    t3 = $[36];
  }
  let t4;
  if ($[37] !== hasStartedActions || $[38] !== isUserAgreed || $[39] !== t3) {
    t4 = <Checkbox checked={isUserAgreed} className="size-5" disabled={hasStartedActions} id="cancel-all-orders-confirmation" onClick={t3} />;
    $[37] = hasStartedActions;
    $[38] = isUserAgreed;
    $[39] = t3;
    $[40] = t4;
  } else {
    t4 = $[40];
  }
  let t5;
  if ($[41] !== chainName || $[42] !== t) {
    t5 = t("confirmIntention", {
      chainName
    });
    $[41] = chainName;
    $[42] = t;
    $[43] = t5;
  } else {
    t5 = $[43];
  }
  let t6;
  if ($[44] !== t5) {
    t6 = <Label htmlFor="cancel-all-orders-confirmation" size="sm" weight="regular">{t5}</Label>;
    $[44] = t5;
    $[45] = t6;
  } else {
    t6 = $[45];
  }
  let t7;
  if ($[46] !== t4 || $[47] !== t6) {
    t7 = <FlexStart className="items-center">{t4}  {t6}</FlexStart>;
    $[46] = t4;
    $[47] = t6;
    $[48] = t7;
  } else {
    t7 = $[48];
  }
  let t8;
  if ($[49] !== t2 || $[50] !== t7) {
    t8 = <BottomSheetBody className="flex flex-col gap-6">{t2}{t7}</BottomSheetBody>;
    $[49] = t2;
    $[50] = t7;
    $[51] = t8;
  } else {
    t8 = $[51];
  }
  return t8;
};
const CancelAllOrdersFlowHeader = t0 => {
  const $ = _c(5);
  const {
    chainName
  } = t0;
  const t = useTranslations("wallet.CancelAllOrdersFlow");
  let t1;
  if ($[0] !== chainName || $[1] !== t) {
    t1 = t("headerTitle", {
      chainName
    });
    $[0] = chainName;
    $[1] = t;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== t1) {
    t2 = <BottomSheetHeader><BottomSheetHeaderTitle>{t1}</BottomSheetHeaderTitle></BottomSheetHeader>;
    $[3] = t1;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  return t2;
};
export const CancelAllOrdersFlowSkeleton = t0 => {
  const $ = _c(6);
  const {
    chainName
  } = t0;
  let t1;
  if ($[0] !== chainName) {
    t1 = <CancelAllOrdersFlowHeader chainName={chainName} />;
    $[0] = chainName;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <BottomSheetBody className="flex flex-col gap-6"><SkeletonLine className="h-5 w-[300px]" /><SkeletonLine className="h-5 w-[300px]" /></BottomSheetBody>;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <BottomSheetFooter><FlexEnd><SkeletonLine className="h-10 w-[224px]" /></FlexEnd></BottomSheetFooter>;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== t1) {
    t4 = <>{t1}{t2}{t3}</>;
    $[4] = t1;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  return t4;
};
const CancelAllOrdersStatus = () => {
  const $ = _c(12);
  const t = useTranslations("wallet.CancelAllOrdersFlow");
  const status = useFlowFinalTransactionStatus();
  if (status === "pending") {
    let t0;
    if ($[0] !== t) {
      t0 = t("status.pending");
      $[0] = t;
      $[1] = t0;
    } else {
      t0 = $[1];
    }
    let t1;
    if ($[2] !== t0) {
      t1 = <TransactionFullScreen title={t0} />;
      $[2] = t0;
      $[3] = t1;
    } else {
      t1 = $[3];
    }
    return t1;
  }
  if (status === "success") {
    let t0;
    if ($[4] !== t) {
      t0 = t("status.success");
      $[4] = t;
      $[5] = t0;
    } else {
      t0 = $[5];
    }
    let t1;
    if ($[6] !== t0) {
      t1 = <TransactionFullScreen title={t0} />;
      $[6] = t0;
      $[7] = t1;
    } else {
      t1 = $[7];
    }
    return t1;
  }
  let t0;
  if ($[8] !== t) {
    t0 = t("status.error");
    $[8] = t;
    $[9] = t0;
  } else {
    t0 = $[9];
  }
  let t1;
  if ($[10] !== t0) {
    t1 = <TransactionFullScreen title={t0} />;
    $[10] = t0;
    $[11] = t1;
  } else {
    t1 = $[11];
  }
  return t1;
};